import isEmpty from 'lodash/isEmpty'
import { useMemo, useCallback } from 'react'
import { TaskStatusEnum, TaskStatus } from '@ztechapps/common.contract'
import { HorizontalStack, resolveStatusColor, ShowIf, Spinner } from '@ztechapps/webui.util'
import { useTaskFilterBarContext } from './Context'
import styles from './TaskFilterBar.module.scss'

const ALL_TASK_STATUSES = Object.values(TaskStatusEnum)

type StatusTabConfig = {
  key: 'all' | 'draft' | 'inProgress' | 'complete' | 'cancelled'
  label: string
  statuses: TaskStatusEnum[]
  color?: string
}

const STATUS_TABS: StatusTabConfig[] = [
  { key: 'all', label: 'All', statuses: ALL_TASK_STATUSES },
  { key: 'draft', label: 'Draft', statuses: [TaskStatusEnum.Draft], color: resolveStatusColor(TaskStatus.Draft)?.fill },
  {
    key: 'inProgress',
    label: 'In Progress',
    statuses: [TaskStatusEnum.InProgress],
    color: resolveStatusColor(TaskStatus.InProgress)?.fill,
  },
  {
    key: 'complete',
    label: 'Complete',
    statuses: [TaskStatusEnum.Complete],
    color: resolveStatusColor(TaskStatus.Complete)?.fill,
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    statuses: [TaskStatusEnum.Cancelled],
    color: resolveStatusColor(TaskStatus.Cancelled)?.fill,
  },
]

export const TaskStatusSummaryTabs = () => {
  const { filterState, setFilter, statusCounts, statusCountsLoading } = useTaskFilterBarContext()

  const activeTabKeys = useMemo(() => {
    const active = new Set<string>()
    const selectedStatuses = filterState.statuses

    if (selectedStatuses.length === ALL_TASK_STATUSES.length) {
      active.add('all')
    }

    STATUS_TABS.forEach((tab) => {
      if (tab.key === 'all') return
      const tabStatus = tab.statuses[0]
      if (selectedStatuses.includes(tabStatus)) {
        active.add(tab.key)
      }
    })

    return active
  }, [filterState.statuses])

  const handleTabClick = useCallback(
    (tab: StatusTabConfig) => {
      if (tab.key === 'all') {
        setFilter({ statuses: ALL_TASK_STATUSES })
        return
      }

      const tabStatus = tab.statuses[0]
      const currentStatuses = filterState.statuses
      const isCurrentlySelected = currentStatuses.includes(tabStatus)

      if (isCurrentlySelected) {
        // Remove this status, but ensure at least one remains
        const remaining = currentStatuses.filter((s) => s !== tabStatus)
        if (!isEmpty(remaining)) {
          setFilter({ statuses: remaining })
        }
      } else {
        // Add this status
        setFilter({ statuses: [...currentStatuses, tabStatus] })
      }
    },
    [filterState.statuses, setFilter]
  )

  const getCount = (key: StatusTabConfig['key']): number => {
    return statusCounts[key]
  }

  return (
    <HorizontalStack className="border-bottom overflow-auto mb-2">
      {STATUS_TABS.map((tab, index) => {
        const isActive = activeTabKeys.has(tab.key)
        const count = getCount(tab.key)

        return (
          <HorizontalStack key={tab.key}>
            <ShowIf condition={index > 0}>
              <div className={`${styles.tabSeparator} flex-shrink-0`} />
            </ShowIf>
            <button
              type="button"
              className={`${styles.statusTab} d-flex align-items-center border-top-0 border-start-0 border-end-0 bg-transparent text-secondary fw-medium text-nowrap ${isActive ? styles.statusTabActive : ''}`}
              onClick={() => handleTabClick(tab)}
              style={isActive && tab.color ? { borderBottomColor: tab.color } : undefined}
            >
              <span>{tab.label}</span>
              <ShowIf condition={statusCountsLoading}>
                <Spinner size="sm" className={styles.statusTabSpinner} />
              </ShowIf>
              <ShowIf condition={!statusCountsLoading}>
                <small className="fw-semibold">{count}</small>
              </ShowIf>
            </button>
          </HorizontalStack>
        )
      })}
    </HorizontalStack>
  )
}
